import React, { useState, useEffect, useRef } from 'react';
import {
  Mail,
  Lock,
  KeyRound,
  ArrowRight,
  CheckCircle2,
  ShieldAlert,
  ArrowLeft,
  User,
  Briefcase,
  Eye,
  EyeOff,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { JobMatchLogo } from '../../components/common/JobMatchLogo';
import { supabase } from '../../lib/supabaseService';
import { PasswordStrengthIndicator } from '../../components/common/PasswordStrengthIndicator';
import { PasswordRequirements } from '../../components/common/PasswordRequirements';

type FlowState = 'checking' | 'request' | 'reset_form' | 'expired' | 'success';

export const PasswordResetPage: React.FC = () => {
  const { navigateTo, viewParams } = useApp();

  const [flowState, setFlowState] = useState<FlowState>('checking');
  const [accountRole, setAccountRole] = useState<'customer' | 'professional'>(
    viewParams.role === 'professional' ? 'professional' : 'customer'
  );
  const [email, setEmail] = useState(viewParams.email || '');
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [error, setError] = useState('');

  // New Password State
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverRecoveryToken, setServerRecoveryToken] = useState('');

  // Ref to prevent double code exchange in React Strict Mode / double effect renders
  const processingRef = useRef(false);

  // Parse recovery tokens / code / session on mount
  useEffect(() => {
    let isMounted = true;

    const verifyRecoverySession = async () => {
      try {
        // 1. Check if a valid authenticated session ALREADY exists (e.g. from Supabase client auto-detection)
        const { data: initialSessionData } = await supabase.auth.getSession();
        if (initialSessionData?.session?.user) {
          if (initialSessionData.session.user.email && isMounted) {
            setEmail(initialSessionData.session.user.email);
          }
          if (isMounted) {
            setFlowState('reset_form');
          }
          return;
        }

        // 2. Parse URL parameters from search AND hash
        const hash = window.location.hash.replace(/^#/, '');
        const hashParams = new URLSearchParams(hash);
        const searchParams = new URLSearchParams(window.location.search);

        const emailInUrl =
          searchParams.get('email') ||
          hashParams.get('email') ||
          viewParams.email ||
          '';

        if (emailInUrl && isMounted) {
          setEmail(emailInUrl);
        }

        const urlError =
          hashParams.get('error_description') ||
          hashParams.get('error') ||
          searchParams.get('error_description') ||
          searchParams.get('error');

        if (urlError) {
          const lower = urlError.toLowerCase();
          if (
            lower.includes('expired') ||
            lower.includes('invalid') ||
            lower.includes('revoked') ||
            lower.includes('denied')
          ) {
            if (isMounted) setFlowState('expired');
            return;
          }
        }

        const code = searchParams.get('code') || hashParams.get('code');
        const recoveryTokenParam =
          searchParams.get('recovery_token') || hashParams.get('recovery_token') || '';
        const tokenHash =
          searchParams.get('token_hash') ||
          hashParams.get('token_hash') ||
          searchParams.get('token') ||
          hashParams.get('token');
        const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        const isTypeRecovery =
          searchParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery';
        const hasRecoveryParams = Boolean(
          code || recoveryTokenParam || tokenHash || accessToken || isTypeRecovery
        );

        if (recoveryTokenParam) {
          if (isMounted) {
            setServerRecoveryToken(recoveryTokenParam);
            setFlowState('reset_form');
          }
          return;
        }

        if (!hasRecoveryParams) {
          // Grace period for Supabase client auto-detection on mount before showing request form
          await new Promise((res) => setTimeout(res, 300));
          if (!isMounted) return;
          const { data: delayedSession } = await supabase.auth.getSession();
          if (delayedSession?.session?.user) {
            if (delayedSession.session.user.email && isMounted) {
              setEmail(delayedSession.session.user.email);
            }
            if (isMounted) setFlowState('reset_form');
            return;
          }
          if (isMounted) setFlowState('request');
          return;
        }

        // 3. Process URL recovery callback if params exist and haven't been processed yet
        if (!processingRef.current) {
          processingRef.current = true;

          // Flow A: PKCE Code Exchange
          if (code) {
            try {
              // Check if auto-detection already established the session while mounting
              const { data: preExchangeSession } = await supabase.auth.getSession();
              if (preExchangeSession?.session?.user && isMounted) {
                if (preExchangeSession.session.user.email) {
                  setEmail(preExchangeSession.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              const { data: exchangeData, error: exchangeErr } =
                await supabase.auth.exchangeCodeForSession(code);

              if (!isMounted) return;

              if (!exchangeErr && exchangeData?.session) {
                if (exchangeData.session.user?.email) {
                  setEmail(exchangeData.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              // If exchangeCodeForSession failed (e.g. race with client auto-detection), check getSession()
              const { data: recheckData } = await supabase.auth.getSession();
              if (recheckData?.session && isMounted) {
                if (recheckData.session.user?.email) {
                  setEmail(recheckData.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              // Poll once more after brief delay for in-flight auto-detection
              await new Promise((res) => setTimeout(res, 400));
              if (!isMounted) return;

              const { data: finalSession } = await supabase.auth.getSession();
              if (finalSession?.session && isMounted) {
                if (finalSession.session.user?.email) {
                  setEmail(finalSession.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              // Genuinely expired or invalid code
              if (isMounted) setFlowState('expired');
              return;
            } catch (e) {
              console.warn('PKCE exchange handling notice:', e);
              const { data: errCheck } = await supabase.auth.getSession();
              if (errCheck?.session && isMounted) {
                if (errCheck.session.user?.email) setEmail(errCheck.session.user.email);
                setFlowState('reset_form');
                return;
              }
              if (isMounted) setFlowState('expired');
              return;
            }
          }

          // Flow B: OTP Token Hash Verification
          if (tokenHash) {
            try {
              const tokenType = (searchParams.get('type') || hashParams.get('type') || 'recovery') as any;
              let verifyData: any;
              let verifyErr: any;

              // 1. Primary verification via token_hash
              const res1 = await supabase.auth.verifyOtp({
                token_hash: tokenHash,
                type: tokenType,
              });
              verifyData = res1.data;
              verifyErr = res1.error;

              // 2. Fallback to token + email if token_hash returned error
              if (verifyErr && emailInUrl) {
                const res2 = await supabase.auth.verifyOtp({
                  email: emailInUrl,
                  token: tokenHash,
                  type: tokenType,
                });
                if (!res2.error && res2.data?.session) {
                  verifyData = res2.data;
                  verifyErr = null;
                }
              }

              if (!isMounted) return;

              if (!verifyErr && verifyData?.session) {
                if (verifyData.session.user?.email) {
                  setEmail(verifyData.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              const { data: recheckData } = await supabase.auth.getSession();
              if (recheckData?.session && isMounted) {
                if (recheckData.session.user?.email) {
                  setEmail(recheckData.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              if (isMounted) setFlowState('expired');
              return;
            } catch (e) {
              console.warn('Verify OTP handling notice:', e);
              if (isMounted) setFlowState('expired');
              return;
            }
          }

          // Flow C: Implicit Flow Access Token
          if (accessToken) {
            try {
              const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token') || '';
              const { data: setData, error: setErr } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });

              if (!isMounted) return;

              if (!setErr && setData?.session) {
                if (setData.session.user?.email) {
                  setEmail(setData.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              const { data: recheckData } = await supabase.auth.getSession();
              if (recheckData?.session && isMounted) {
                if (recheckData.session.user?.email) {
                  setEmail(recheckData.session.user.email);
                }
                setFlowState('reset_form');
                return;
              }

              if (isMounted) setFlowState('expired');
              return;
            } catch (e) {
              console.warn('Set session handling notice:', e);
              if (isMounted) setFlowState('expired');
              return;
            }
          }
        }
      } catch (err) {
        console.warn('Password recovery verification error:', err);
        if (isMounted) setFlowState('request');
      }
    };

    verifyRecoverySession();

    // Listen for auth state events from Supabase client auto-detection
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && session?.user) {
        if (session.user?.email && isMounted) {
          setEmail(session.user.email);
        }
        if (isMounted) {
          setFlowState('reset_form');
        }
      }
    });

    return () => {
      isMounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, [viewParams.email]);

  // Handler 1: Request Password Reset Email
  const handleSendResetEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setRequestSent(false);

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError('Please enter a valid registered email address.');
      return;
    }

    setLoading(true);
    try {
      const isProdHost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'jobmatch.ng' || window.location.hostname === 'www.jobmatch.ng');
      const redirectUrl = isProdHost
        ? 'https://jobmatch.ng/reset-password'
        : `${window.location.origin}/reset-password`;

      let dispatched = false;
      try {
        const res = await fetch('/api/auth/send-password-reset-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: cleanEmail,
            resetUrl: redirectUrl,
            recipientName: cleanEmail.split('@')[0],
          }),
        });
        const json = await res.json().catch(() => null);
        if (json?.success && json?.dispatchedWithToken) {
          dispatched = true;
        }
      } catch (_) {}

      // Fallback or primary: Supabase official resetPasswordForEmail
      if (!dispatched) {
        await supabase.auth.resetPasswordForEmail(cleanEmail, {
          redirectTo: redirectUrl,
        }).catch(() => {});
      }

      setRequestSent(true);
    } catch (_err) {
      setRequestSent(true);
    } finally {
      setLoading(false);
    }
  };

  // Handler 2: Submit New Password (Requires active recovery session)
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match. Please ensure both fields are identical.');
      return;
    }

    setLoading(true);
    try {
      // 1. Check if server-issued recovery_token is present or if active Supabase recovery session exists
      const { data: activeSessionData } = await supabase.auth.getSession();

      if (serverRecoveryToken && !activeSessionData?.session) {
        const directRes = await fetch('/api/auth/reset-password-direct', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            newPassword,
            recoveryToken: serverRecoveryToken,
          }),
        });
        const directData = await directRes.json().catch(() => ({}));
        if (!directRes.ok || !directData?.success) {
          const msg = String(directData?.error || '');
          if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid') || directRes.status === 401) {
            setError('Your password reset link has expired or is invalid. Please request a new reset link.');
            setFlowState('expired');
            return;
          }
          throw new Error(directData?.error || 'Unable to update password. Please request a new link.');
        }

        try {
          if (typeof window !== 'undefined') {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (_) {}

        setFlowState('success');
        return;
      }

      if (!activeSessionData?.session) {
        setError('Your password reset session has expired or is invalid. Please request a new reset link.');
        setFlowState('expired');
        return;
      }

      // 2. Perform authenticated Supabase password update
      const { error: updateErr } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateErr) {
        const msg = updateErr.message || '';
        if (
          msg.toLowerCase().includes('auth session missing') ||
          msg.toLowerCase().includes('session') ||
          msg.toLowerCase().includes('jwt') ||
          msg.toLowerCase().includes('unauthorized') ||
          msg.toLowerCase().includes('missing')
        ) {
          setError('Your password reset session has expired or is invalid. Please request a new reset link.');
          setFlowState('expired');
          return;
        }
        throw new Error(msg || 'Unable to update password. Please request a new link.');
      }

      // 3. Clean URL parameters after successful password update
      try {
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (_) {}

      // 4. Send branded security notification via Brevo
      const userEmail = activeSessionData?.session?.user?.email || email;
      if (userEmail) {
        fetch('/api/auth/notify-password-changed', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userEmail,
            role: accountRole,
            eventType: 'password_reset',
          }),
        }).catch(() => {});
      }

      setFlowState('success');
    } catch (err: any) {
      const errorMsg =
        err?.message ||
        'Unable to update password. Your reset link may have expired or is invalid. Please request a new link.';

      if (errorMsg.toLowerCase().includes('auth session missing')) {
        setError('Your password reset session has expired or is invalid. Please request a new reset link.');
        setFlowState('expired');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handler 3: Restart Reset Process (From Expired State)
  const handleRestartRequest = () => {
    setError('');
    setRequestSent(false);
    setNewPassword('');
    setConfirmPassword('');
    try {
      window.history.replaceState({}, document.title, '/reset-password');
    } catch (_) {}
    setFlowState('request');
  };

  return (
    <div className="max-w-md mx-auto my-8 sm:my-12 px-4">
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl">
        {/* Header Logo */}
        <div className="flex justify-center mb-6 cursor-pointer" onClick={() => navigateTo('home')}>
          <JobMatchLogo variant="compact" size="lg" />
        </div>

        {/* 1. Checking Session State */}
        {flowState === 'checking' && (
          <div className="text-center py-10 space-y-4">
            <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-xs font-semibold text-slate-600">Verifying password reset security link...</p>
          </div>
        )}

        {/* 2. Expired / Invalid Reset Link State */}
        {flowState === 'expired' && (
          <div className="text-center space-y-5 py-2">
            <div className="w-14 h-14 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center mx-auto border border-rose-100">
              <AlertTriangle className="w-8 h-8 text-rose-600" />
            </div>

            <div className="space-y-2">
              <h1 className="text-xl font-extrabold text-slate-900">Password Reset Link Invalid or Expired</h1>
              <p className="text-xs text-slate-600 leading-relaxed max-w-xs mx-auto">
                This password reset link is invalid or has expired. Password recovery links can only be used once and expire for your security.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="button"
                onClick={handleRestartRequest}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Request New Reset Link</span>
              </button>
            </div>
          </div>
        )}

        {/* 3. Password Updated Success State */}
        {flowState === 'success' && (
          <div className="text-center space-y-5 py-2">
            <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto border border-emerald-100">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>

            <div className="space-y-2">
              <h1 className="text-xl font-extrabold text-slate-900">Password Updated Successfully</h1>
              <p className="text-xs text-slate-600 leading-relaxed">
                Your JobMatch NG account password has been updated. You can now log in with your new password.
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-3">
              <button
                type="button"
                onClick={() => navigateTo('customer_auth', { authMode: 'login' })}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <User className="w-4 h-4" />
                <span>Proceed to Customer Sign In</span>
              </button>
              <button
                type="button"
                onClick={() => navigateTo('pro_auth', { authMode: 'login' })}
                className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <Briefcase className="w-4 h-4" />
                <span>Proceed to Professional Sign In</span>
              </button>
            </div>
          </div>
        )}

        {/* 4. Reset Form State (Active Verified Recovery Session) */}
        {flowState === 'reset_form' && (
          <div>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-emerald-100">
                <KeyRound className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">Create a New Password</h1>
              <p className="text-xs text-slate-500 mt-1">Choose a strong password for your JobMatch NG account.</p>
            </div>

            <form onSubmit={handleUpdatePassword} className="space-y-4 text-xs">
              {error && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {email && (
                <div className="p-3 rounded-xl bg-emerald-50/70 border border-emerald-200/80 text-xs text-emerald-900 flex items-center justify-between">
                  <div>
                    <span className="text-emerald-700/80 block text-[11px] font-medium">Updating credentials for:</span>
                    <span className="font-bold text-slate-900">{email}</span>
                  </div>
                </div>
              )}

              <div>
                <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">New Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    placeholder="Enter new secure password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrengthIndicator password={newPassword} />
                <PasswordRequirements password={newPassword} className="mt-2" />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">Confirm New Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 cursor-pointer"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl text-sm shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer mt-4"
              >
                {loading ? 'Updating Password...' : 'Update Password'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        )}

        {/* 5. Request Reset Link Flow (Forgot Password Request Screen) */}
        {flowState === 'request' && (
          <div>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-emerald-100">
                <KeyRound className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900">Forgot your password?</h1>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                Enter the email address associated with your JobMatch NG account. We'll send you a secure link to reset your password.
              </p>
            </div>

            {/* Account Role Selector */}
            <div className="flex rounded-xl bg-slate-100 p-1 mb-5 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setAccountRole('customer')}
                className={`flex-1 py-2 rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  accountRole === 'customer'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <User className="w-3.5 h-3.5" />
                <span>Customer Account</span>
              </button>
              <button
                type="button"
                onClick={() => setAccountRole('professional')}
                className={`flex-1 py-2 rounded-lg transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                  accountRole === 'professional'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                <Briefcase className="w-3.5 h-3.5" />
                <span>Professional Trade</span>
              </button>
            </div>

            {error && (
              <div className="p-3 mb-4 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {requestSent ? (
              <div className="text-center space-y-4 py-2">
                <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto border border-emerald-100">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>

                <div className="space-y-1.5">
                  <h3 className="text-base font-bold text-slate-900">Check Your Email</h3>
                  <p className="text-xs text-slate-600 leading-relaxed max-w-sm mx-auto">
                    If an account exists for this email, a password reset link has been sent. Please check your inbox and spam folder.
                  </p>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl text-[11px] text-slate-500 border border-slate-100">
                  Didn't receive the email? Check your spam/junk folder or wait a minute before trying again.
                </div>

                <div className="flex flex-col gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRequestSent(false);
                      setError('');
                    }}
                    className="w-full py-2.5 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-all cursor-pointer"
                  >
                    Resend to a Different Email
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      navigateTo(accountRole === 'professional' ? 'pro_auth' : 'customer_auth', {
                        authMode: 'login',
                      })
                    }
                    className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Back to Sign In</span>
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSendResetEmail} className="space-y-4 text-xs">
                <div>
                  <label className="block font-semibold text-slate-700 dark:text-slate-200 mb-1">
                    Registered Email Address
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="email"
                      required
                      placeholder={accountRole === 'professional' ? 'pro@jobmatch.ng' : 'customer@example.com'}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl text-sm focus:outline-hidden focus:border-blue-500"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-xl text-sm shadow-md shadow-emerald-600/20 transition-all flex items-center justify-center gap-2 cursor-pointer mt-4"
                >
                  {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
                  <ArrowRight className="w-4 h-4" />
                </button>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={() => navigateTo('customer_auth', { authMode: 'login' })}
                    className="text-slate-500 hover:text-emerald-700 font-medium cursor-pointer"
                  >
                    Back to Sign In
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
